/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import {
  useFetchOrdersData,
  useFetchOrderTemplate,
  useOrderBulkDocMutation,
  useFetchFmOrderTemplate,
  useFmOrderBulkDocMutation,
} from "../../../apis/orderApis";
import toast from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";

export default function BulkUploadModals({ onClose }: { onClose: () => void }) {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [orderType, setOrderType] = useState<"LM" | "FM">("LM");

  const { mutate: uploadLMDocMutate, isPending: isLMBulkUploading } = useOrderBulkDocMutation();
  const { mutate: uploadFMDocMutate, isPending: isFMBulkUploading } = useFmOrderBulkDocMutation();
  const isBulkUploading = orderType === "LM" ? isLMBulkUploading : isFMBulkUploading;

  const { data: templateData, refetch: fetchTemplates } = useFetchOrderTemplate();
  const { data: fmTemplateData, refetch: fetchFmTemplates } = useFetchFmOrderTemplate();

  const {
    refetch,
  } = useFetchOrdersData(
    pageNo,
    limit,
    searchFor,
    activeTab === "Reverse",
    activeTab === "Cancelled",
    activeTab === "Deleted"
  );

  const handleUploadDoc = () => {
    if (!file) {
      toast.error(t("Please select a file first"));
      return;
    }

    const uploadMutate = orderType === "LM" ? uploadLMDocMutate : uploadFMDocMutate;
    uploadMutate({ orders: file }, {
      onSuccess: (data: any) => {
        onClose();
        refetch();
        toast.success(t(`${orderType} Orders uploaded successfully`));
      },
      onError: (error: any) => {
        console.log(error, "error");
        toast.error(t(error?.message || error || "Failed to upload orders"));
      },
    });
  };

  const handleDownloadTemplate = () => {
    if (orderType === "LM") {
      if (templateData && templateData.length > 0) {
        try {
          const headings = Object.keys(templateData[0]);
          const csvContent = headings.join(",") + "\n";

          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "orders-template.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          toast.success(t("CSV template downloaded successfully"));
        } catch (err) {
          console.error("CSV generation error:", err);
          toast.error(t("Failed to generate CSV template"));
        }
      } else {
        toast.error(t("Template not available"));
      }
    } else {
      try {
        const headings = (fmTemplateData && fmTemplateData[0])
          ? Object.keys(fmTemplateData[0])
          : [
              "client_id",
              "reference_no",
              "receiver_name",
              "receiver_mobile",
              "receiver_email",
              "receiver_country",
              "receiver_city",
              "receiver_district",
              "receiver_address",
              "receiver_national_address",
              "shipment_cost",
              "payment_type",
              "cod_amount",
              "no_of_pieces",
              "service",
              "description",
              "latitude",
              "longitude",
              "product_id_1",
              "quantity_1",
              "product_id_2",
              "quantity_2",
              "product_id_3",
              "quantity_3",
              "product_id_4",
              "quantity_4",
              "product_id_5",
              "quantity_5",
            ];
        const csvContent = headings.join(",") + "\n";

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "fm-orders-template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(t("FM CSV template downloaded successfully"));
      } catch (err) {
        console.error("CSV generation error:", err);
        toast.error(t("Failed to generate FM CSV template"));
      }
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchFmTemplates();
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="rounded-2xl w-full max-w-[500px] h-[calc(100vh-2rem)] flex flex-col relative bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center flex-shrink-0">
          <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl ">
            <h2 className="text-2xl font-bold">{t("Bulk Upload")} {orderType} {t("Orders")}</h2>
          </div>
          <div className="relative ">
            <button
              onClick={onClose}
              className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 mr-4 flex items-center justify-center flex-shrink-0"
            >
              <IconX size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="w-full flex-1 overflow-y-auto p-4 space-y-4 scrollbar-clean">
          {/* Selection for LM vs FM */}
          <div className="flex gap-4 p-2 bg-[#FCFDF8] border border-[#D2D2D2] rounded-md">
            <span className="text-sm font-semibold text-[#212121] mr-2">{t("Order Type:")}</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="LM"
                checked={orderType === "LM"}
                onChange={() => {
                  setOrderType("LM");
                  setFile(null);
                }}
                className="accent-[#FBD037]"
              />
              <span className="text-sm font-medium text-[#212121]">{t("LM Orders")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="FM"
                checked={orderType === "FM"}
                onChange={() => {
                  setOrderType("FM");
                  setFile(null);
                }}
                className="accent-[#FBD037]"
              />
              <span className="text-sm font-medium text-[#212121]">{t("FM Orders")}</span>
            </label>
          </div>

          <FileUploader
            name="photo"
            extraParentClasses="col-span-3"
            img="/img/upload-img.png"
            title={t("Upload") + " " + orderType + " " + t("CSV File")}
            message={t("Upload a CSV file only (Max size: 2 MB).")}
            classNameMain="bg-transparent border-none flex items-center gap-3"
            fileVal={file}
            setFileVal={(file: any) => {
              setFile(file); 
            }}
            showPreview={true}
            required
          />

          {file && (
            <p className="text-sm text-green-600 font-medium">
              {t("Selected File:")} {file.name}
            </p>
          )}

          <ul className="list-disc px-5 text-sm text-[#1E293B] font-medium space-y-1">
            <li>{t("Download the Excel/CSV template before uploading.")}</li>
            <li>{t("Do not change column headers.")}</li>
            {orderType === "FM" ? (
              <>
                <li>{t("Include product details (Product ID & Quantity) for each order.")}</li>
                <li>{t("Weight will be calculated from products - do not include weight column.")}</li>
              </>
            ) : (
              <li>{t("Mandatory fields: Client ID, Receiver, Reference, Weight, etc.")}</li>
            )}
          </ul>

          <div className="flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2]">
            <p className="text-xs text-[#212121] font-medium">
              <b>{t("Note")}</b> : {t("Please upload the file in the same format as the downloaded file.")}
            </p>
            <button onClick={handleDownloadTemplate} className="bg-[#000000] text-white flex items-center space-x-1 p-2 rounded-md text-sm cursor-pointer">
              <img src="/img/csvxl.svg" alt="" />
              <span>{t("Download")}</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end gap-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto h-11 px-6 rounded-full border border-[#D8D8D8] bg-white hover:bg-[#F5F5F5] transition-all duration-200 font-medium cursor-pointer"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleUploadDoc}
            disabled={isBulkUploading || !file}
            className="w-full sm:w-auto min-w-[140px] h-11 px-6 rounded-full font-semibold transition-all duration-200 flex items-center justify-center gap-2 bg-[#FBD037] hover:bg-[#f7c400] text-black shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBulkUploading ? t("Uploading...") : t("Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}